/**
 * CounselPanel — ask Regulatory Counsel (the MA/Medicaid attorney agent) a
 * question about a specific PPM entity and read its grounded opinion memos.
 *
 * Honesty rules baked into the UI:
 *   - Verified citations are shown with their verbatim quotes; the panel
 *     says how many citations were dropped by verification.
 *   - Ungrounded opinions carry a visible warning, never quiet confidence.
 *   - Every opinion is advisory and shows its acknowledgment state.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { ScaleIcon, CheckBadgeIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const API = process.env.REACT_APP_API_URL;

const CONF_STYLE = {
  high:   'bg-emerald-50 text-emerald-700 border-emerald-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  low:    'bg-red-50 text-red-700 border-red-200',
};

function OpinionCard({ row, onAcknowledged }) {
  const [open, setOpen] = useState(false);
  const [ackErr, setAckErr] = useState('');
  const [pendingFeedback, setPendingFeedback] = useState(null); // 'sound' | 'off_base'
  const [feedbackNote, setFeedbackNote] = useState('');
  const [savingFeedback, setSavingFeedback] = useState(false);
  const [feedbackErr, setFeedbackErr] = useState('');
  const op = typeof row.opinion === 'string' ? JSON.parse(row.opinion) : row.opinion;
  const declined = row.declined || op.declined === true;

  const sendFeedback = async () => {
    if (!pendingFeedback || savingFeedback) return;
    setSavingFeedback(true); setFeedbackErr('');
    try {
      const res = await fetch(`${API}/api/ppm/counsel/opinions/${row.id}/feedback`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ feedback: pendingFeedback, note: feedbackNote.trim() || null }),
      });
      const data = await res.json();
      if (!data.success) { setFeedbackErr(data.message || 'Only compliance admins can review opinions.'); }
      else { setPendingFeedback(null); setFeedbackNote(''); onAcknowledged(); }
    } catch { setFeedbackErr('Failed to save the review.'); }
    setSavingFeedback(false);
  };

  const acknowledge = async () => {
    setAckErr('');
    try {
      const res = await fetch(`${API}/api/ppm/counsel/opinions/${row.id}/acknowledge`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!data.success) { setAckErr(data.message || 'Only compliance admins can acknowledge opinions.'); return; }
      onAcknowledged();
    } catch { setAckErr('Failed to acknowledge.'); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <button onClick={() => setOpen(o => !o)} className="w-full text-left p-4 hover:bg-slate-50/50 transition-colors">
        <div className="flex items-center gap-2 flex-wrap mb-1.5">
          {declined ? (
            <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold border bg-slate-100 text-slate-600 border-slate-300">
              Declined to opine — not enough verified law
            </span>
          ) : (
            <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${CONF_STYLE[op.confidence] || CONF_STYLE.medium}`}>
              {op.confidence} confidence
            </span>
          )}
          {row.feedback && (
            <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${row.feedback === 'sound' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
              {row.feedback === 'sound' ? 'Reviewed: sound advice' : 'Reviewed: off base'}{row.feedback_by_name ? ` · ${row.feedback_by_name}` : ''}
            </span>
          )}
          {!declined && row.grounded ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-orange-50 text-orange-700 border border-orange-200">
              <CheckBadgeIcon className="w-3.5 h-3.5" /> {row.citations_verified} verified citation{row.citations_verified === 1 ? '' : 's'}
            </span>
          ) : !declined ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-50 text-red-700 border border-red-200">
              <ExclamationTriangleIcon className="w-3.5 h-3.5" /> ungrounded
            </span>
          ) : null}
          <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${row.status === 'acknowledged' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-500 border-slate-200'}`}>
            {row.status === 'acknowledged' ? `Acknowledged${row.acknowledged_by_name ? ' · ' + row.acknowledged_by_name : ''}` : 'Awaiting review'}
          </span>
          <span className="text-[11px] text-slate-400 ml-auto">{new Date(row.created_at).toLocaleString()}</span>
        </div>
        <p className="text-sm font-medium text-slate-700">{op.question_presented || row.question}</p>
        <p className="text-sm text-slate-600 mt-1 leading-relaxed">{op.short_answer}</p>
      </button>

      {open && (
        <div className="border-t border-slate-100 p-4 space-y-4">
          {declined && (
            <div className="rounded-lg border border-slate-300 bg-slate-50 p-3">
              <p className="text-xs text-slate-600 leading-relaxed">
                Counsel chose not to give an opinion here because it could not verify enough of the controlling law to answer responsibly. What it needs is listed under "Counsel recommends" below. This is deliberate: no answer is better than a guess.
              </p>
            </div>
          )}
          {op.analysis && (
            <div>
              <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Analysis</div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{op.analysis}</p>
            </div>
          )}

          {(op.citations || []).length > 0 && (
            <div>
              <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-2">Verified citations</div>
              <div className="space-y-2">
                {op.citations.map((c, i) => (
                  <div key={i} className="rounded-lg border border-orange-200 bg-orange-50/40 p-3">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs font-bold text-orange-700">{c.citation}</span>
                      {c.source_url && (
                        <a href={c.source_url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-orange-600 hover:underline">eCFR ↗</a>
                      )}
                    </div>
                    <p className="text-xs text-slate-700 font-mono mt-1.5 leading-relaxed">"{c.quote}"</p>
                    {c.relevance && <p className="text-[11px] text-slate-500 mt-1">{c.relevance}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {(op.recommended_actions || []).length > 0 && (
            <div>
              <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Counsel recommends</div>
              <ul className="space-y-1">
                {op.recommended_actions.map((a, i) => (
                  <li key={i} className="text-sm text-slate-700 flex gap-2"><span className="text-orange-500 flex-shrink-0">•</span>{a}</li>
                ))}
              </ul>
            </div>
          )}

          {(op.caveats || []).length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3">
              <div className="text-[10px] font-semibold text-amber-700 uppercase tracking-wide mb-1">Caveats</div>
              <ul className="space-y-1">
                {op.caveats.map((c, i) => (
                  <li key={i} className="text-xs text-slate-600">{c}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Feedback — the plain-language review that teaches Counsel when to
              be confident. One click, optional note, compliance admins only. */}
          {!row.feedback && !declined && (
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
              <p className="text-xs font-medium text-slate-600 mb-2">Was this advice right? Your review teaches Counsel when to be confident.</p>
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={() => setPendingFeedback('sound')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${pendingFeedback === 'sound' ? 'bg-emerald-600 text-white border-emerald-600' : 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'}`}>
                  Sound advice
                </button>
                <button onClick={() => setPendingFeedback('off_base')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${pendingFeedback === 'off_base' ? 'bg-red-600 text-white border-red-600' : 'border-red-300 text-red-700 hover:bg-red-50'}`}>
                  Off base
                </button>
                {pendingFeedback && (
                  <>
                    <input
                      value={feedbackNote} onChange={e => setFeedbackNote(e.target.value)}
                      placeholder={pendingFeedback === 'off_base' ? 'What did it get wrong? (optional, helps it learn)' : 'Anything to add? (optional)'}
                      className="flex-1 min-w-[200px] rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs bg-white" />
                    <button onClick={sendFeedback} disabled={savingFeedback}
                      className="px-3.5 py-1.5 rounded-lg bg-orange-600 text-white text-xs font-semibold hover:bg-orange-700 disabled:opacity-50">
                      {savingFeedback ? 'Saving…' : 'Save review'}
                    </button>
                  </>
                )}
              </div>
              {feedbackErr && <p className="text-xs text-red-600 mt-2">{feedbackErr}</p>}
            </div>
          )}
          {row.feedback && row.feedback_note && (
            <p className="text-xs text-slate-500 italic">Reviewer note: {row.feedback_note}</p>
          )}

          <div className="flex items-center gap-3 pt-1">
            {row.status !== 'acknowledged' && (
              <button onClick={acknowledge}
                className="px-4 py-2 rounded-xl bg-orange-600 text-white text-xs font-semibold hover:bg-orange-700 transition-colors">
                Acknowledge (compliance review)
              </button>
            )}
            {ackErr && <span className="text-xs text-red-600">{ackErr}</span>}
            <span className="text-[11px] text-slate-400 ml-auto">
              Advisory only — Counsel researched {row.steps || '?'} step{row.steps === 1 ? '' : 's'}; every citation shown survived verbatim verification.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CounselPanel({ subjectType, subjectId, defaultQuestion = '', title = 'Ask Regulatory Counsel' }) {
  const [opinions, setOpinions] = useState([]);
  const [question, setQuestion] = useState(defaultQuestion);
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const qs = new URLSearchParams({ subject_type: subjectType });
      if (subjectId) qs.set('subject_id', subjectId);
      const res = await fetch(`${API}/api/ppm/counsel/opinions?${qs}`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setOpinions(data.opinions || []);
    } catch {}
  }, [subjectType, subjectId]);

  useEffect(() => { load(); }, [load]);

  const ask = async () => {
    if (question.trim().length < 10 || asking) return;
    setAsking(true); setError('');
    try {
      const res = await fetch(`${API}/api/ppm/counsel/ask`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: question.trim(), subject_type: subjectType, subject_id: subjectId || null }),
      });
      const data = await res.json();
      if (!data.success) { setError(data.message || 'Counsel could not complete the opinion.'); }
      else { setQuestion(''); await load(); }
    } catch { setError('Request failed.'); }
    setAsking(false);
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5">
      <div className="flex items-center gap-2 mb-1">
        <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center text-white flex-shrink-0">
          <ScaleIcon className="w-4 h-4" strokeWidth={2} />
        </div>
        <h3 className="text-sm font-bold text-slate-800">{title}</h3>
      </div>
      <p className="text-xs text-slate-400 mb-4">
        A specialist agent in Medicare Advantage, Part D, and Medicaid managed care. It researches the verbatim CFR text, your policies, HPMS guidance, and your operational history, then writes an opinion whose citations are machine-verified. Advisory only, a compliance officer reviews every opinion.
      </p>

      <div className="flex gap-2 items-start mb-4">
        <textarea
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="e.g. Does 42 CFR 422.504 require our FDR oversight policy to cover offshore subcontractors?"
          rows={2}
          disabled={asking}
          className="flex-1 rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-700 focus:ring-2 focus:ring-orange-500/40 focus:border-orange-400 outline-none resize-none disabled:opacity-60"
        />
        <button onClick={ask} disabled={asking || question.trim().length < 10}
          className="px-4 py-2.5 rounded-xl bg-orange-600 text-white text-sm font-semibold hover:bg-orange-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex-shrink-0">
          {asking ? 'Researching…' : 'Ask Counsel'}
        </button>
      </div>

      {asking && (
        <div className="mb-4 p-3 rounded-xl bg-orange-50 border border-orange-200 text-orange-700 text-xs">
          Counsel is researching — reading the regulation text, your policies, and guidance. This usually takes under a minute.
        </div>
      )}
      {error && (
        <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs">{error}</div>
      )}

      {opinions.length > 0 && (
        <div className="space-y-3">
          {opinions.map(row => (
            <OpinionCard key={row.id} row={row} onAcknowledged={load} />
          ))}
        </div>
      )}
      {opinions.length === 0 && !asking && (
        <p className="text-xs text-slate-400 italic">No opinions yet for this {subjectType === 'question' ? 'topic' : subjectType.replace('_', ' ')}.</p>
      )}
    </div>
  );
}
